import { Clock } from '@ondit/ui/icons'
import { helpTopicUrl } from '@/lib/help-links'
import {
  defaultTimezone,
  SCHEDULE_FIELD_LABELS as LABELS,
  MINUTE_INTERVALS,
  SCHEDULE_DEFAULTS,
  SCHEDULE_TYPE_OPTIONS,
  SCHEDULE_FIELD_TYPES as SHOWN,
  TIMEZONE_OPTIONS,
  WEEKDAY_OPTIONS,
} from '@/lib/workflows/schedules/options'
import type { BlockConfig } from '@/blocks/types'

export const ScheduleBlock: BlockConfig = {
  type: 'schedule',
  triggerAllowed: true,
  name: 'Schedule',
  description: 'Trigger workflow execution on a schedule',
  docsLink: helpTopicUrl('schedules'),
  longDescription:
    'Integrate Schedule into the workflow. Can trigger a workflow on a schedule configuration.',
  bestPractices: `
  - Prefer the custom cron expression input method over the other schedule configuration methods. 
  - Clarify the timezone if the user doesn't specify it.
  `,
  category: 'triggers',
  bgColor: '#6366F1',
  icon: Clock,

  canvasPresentation: {
    defaultTitle: 'Schedule',
    /*
     * Schedule has no registry trigger to name — the schedule itself is the
     * event — so it declares its own. Each detail is already gated on
     * `scheduleType`, so exactly the branch belonging to the chosen frequency is
     * on the card and the rest drop. The frequency therefore lives in the detail
     * copy rather than in a `scheduleType` chip, whose dropdown labels are form
     * labels — "Runs Every X Minutes every 15 minutes" is what that reads like.
     */
    triggerSentences: {
      default: [
        'Run',
        { text: 'every', field: 'minutesInterval', after: 'minutes', core: true },
        { text: 'hourly, at minute', field: 'hourlyMinute', core: true },
        { text: 'daily at', field: 'dailyTime', core: true },
        { text: 'weekly on', field: 'weeklyDay', core: true },
        { text: 'at', field: 'weeklyDayTime', core: true },
        { text: 'monthly on day', field: 'monthlyDay', core: true },
        { text: 'at', field: 'monthlyTime', core: true },
        { text: 'on cron', field: 'cronExpression', core: true },
      ],
    },
  },
  subBlocks: [
    {
      id: 'scheduleType',
      type: 'dropdown',
      title: LABELS.scheduleType,
      options: SCHEDULE_TYPE_OPTIONS,
      value: () => SCHEDULE_DEFAULTS.scheduleType,
      required: true,
      mode: 'trigger',
    },

    {
      id: 'minutesInterval',
      type: 'dropdown',
      title: LABELS.minutesInterval,
      options: MINUTE_INTERVALS.map((n) => ({ label: n, id: n })),
      value: () => SCHEDULE_DEFAULTS.minutesInterval,
      required: true,
      mode: 'trigger',
      condition: { field: 'scheduleType', value: SHOWN.minutesInterval },
    },

    {
      id: 'hourlyMinute',
      type: 'short-input',
      title: LABELS.hourlyMinute,
      placeholder: '0-59',
      value: () => SCHEDULE_DEFAULTS.hourlyMinute,
      required: true,
      mode: 'trigger',
      condition: { field: 'scheduleType', value: SHOWN.hourlyMinute },
    },

    {
      id: 'dailyTime',
      type: 'time-input',
      title: LABELS.dailyTime,
      value: () => SCHEDULE_DEFAULTS.dailyTime,
      required: true,
      mode: 'trigger',
      condition: { field: 'scheduleType', value: SHOWN.dailyTime },
    },

    {
      id: 'weeklyDay',
      type: 'dropdown',
      title: LABELS.weeklyDay,
      options: WEEKDAY_OPTIONS,
      value: () => SCHEDULE_DEFAULTS.weeklyDay,
      required: true,
      mode: 'trigger',
      condition: { field: 'scheduleType', value: SHOWN.weeklyDay },
    },

    {
      id: 'weeklyDayTime',
      type: 'time-input',
      title: LABELS.weeklyDayTime,
      value: () => SCHEDULE_DEFAULTS.weeklyDayTime,
      required: true,
      mode: 'trigger',
      condition: { field: 'scheduleType', value: SHOWN.weeklyDayTime },
    },

    {
      id: 'monthlyDay',
      type: 'short-input',
      title: LABELS.monthlyDay,
      placeholder: '1-31',
      value: () => SCHEDULE_DEFAULTS.monthlyDay,
      required: true,
      mode: 'trigger',
      condition: { field: 'scheduleType', value: SHOWN.monthlyDay },
    },

    {
      id: 'monthlyTime',
      type: 'time-input',
      title: LABELS.monthlyTime,
      value: () => SCHEDULE_DEFAULTS.monthlyTime,
      required: true,
      mode: 'trigger',
      condition: { field: 'scheduleType', value: SHOWN.monthlyTime },
    },

    {
      id: 'cronExpression',
      type: 'short-input',
      title: LABELS.cronExpression,
      placeholder: SCHEDULE_DEFAULTS.cronExpression,
      value: () => SCHEDULE_DEFAULTS.cronExpression,
      required: true,
      mode: 'trigger',
      condition: { field: 'scheduleType', value: SHOWN.cronExpression },
      wandConfig: {
        enabled: true,
        prompt: `You are an expert at writing cron expressions. Generate a valid cron expression based on the user's description.

Cron format: minute hour day-of-month month day-of-week
- minute: 0-59
- hour: 0-23
- day-of-month: 1-31, or L for the last day
- month: 1-12 or JAN-DEC
- day-of-week: 0-7 (0 and 7 are Sunday) or SUN-SAT; 1#2 is the second Monday, 1#L the last

Special characters: * (any), , (list), - (range), / (step)
Always exactly five fields. Do not use seconds, W, ?, @ nicknames, or a trailing L on a weekday number.

{context}

Return ONLY the cron expression, nothing else. No explanation, no backticks, no quotes.`,
        placeholder: 'Describe your schedule (e.g., "every weekday at 9am")',
        generationType: 'cron-expression',
      },
    },

    {
      id: 'timezone',
      type: 'dropdown',
      title: LABELS.timezone,
      searchable: true,
      options: TIMEZONE_OPTIONS,
      value: () => defaultTimezone(),
      required: false,
      mode: 'trigger',
      condition: { field: 'scheduleType', value: SHOWN.timezone },
    },

    {
      id: 'scheduleInfo',
      type: 'schedule-info',
      mode: 'trigger',
      hideFromPreview: true,
    },
  ],

  tools: {
    access: [], // No external tools needed
  },

  inputs: {}, // No inputs - schedule triggers initiate workflows

  outputs: {}, // No outputs - schedule triggers initiate workflow execution
}
